import { Base } from "@/atoms-react/text/Text.react";
import { DevicePhoneMobileIcon } from "@heroicons/react/20/solid";
import _ from "lodash";
import type React from "react";
import { useEffect, useState } from "react";
import { twMerge } from "tailwind-merge";
import { AliasCard } from "../alias-card/index.react";
import { useTranslation } from "react-i18next";
import Flag from "@/atoms-react/flag/Flag.react";
import { PHONE_COUNTRY_CODES } from "@/features/utils/constants";
import { getAsYouType } from "awesome-phonenumber";
import { InformationCircleIcon } from "@heroicons/react/24/solid";
import { AliasMobileSchema } from "@/types/user/alias-mobile.types";

type PhoneCardProps = {
  phone: AliasMobileSchema;
  isLoading?: boolean;
  onClick?: () => void;
  onPublicClick?: () => void;
  onDelete?: () => void;
  onDeleteHelper?: string;
  disabled?: boolean;
  dataCy?: string;
} & Omit<React.ComponentPropsWithoutRef<"div">, "onClick">;

export const PhoneCard = ({
  phone,
  onPublicClick,
  dataCy,
  disabled = false,
  ...props
}: PhoneCardProps) => {
  const { t } = useTranslation(["pass"]);
  const [phoneNumber, setPhoneNumber] = useState<string>(phone.phoneNumber);

  useEffect(() => {
    if (!phone.mobileCode) return;

    const country = _.invert(PHONE_COUNTRY_CODES)[phone.mobileCode];
    const asYouType = getAsYouType(country);
    asYouType.reset(phone.phoneNumber);
    const asYouTypePhone = asYouType.getPhoneNumber();
    if (asYouTypePhone.number) {
      setPhoneNumber(asYouTypePhone.number.input);
    }
  }, [phone]);

  return (
    <AliasCard
      tooltipDesignation={t("olkypass.aliasPublicPage.this_phone_number")}
      isPublic={phone.public}
      isMain={phone.main}
      icon={DevicePhoneMobileIcon}
      onPublicClick={onPublicClick}
      {...props}
    >
      {phone.main && (
        <div
          className={twMerge(
            "flex overflow-hidden text-ellipsis whitespace-nowrap break-normal font-semibold"
          )}
        >
          {t("aliasMobilePage.main.title")}
          <div data-tooltip={t("aliasMobilePage.main.description")} data-position="bottom">
            <InformationCircleIcon className={`mr-1 h-4 w-4`} />
          </div>
          {":"}
        </div>
      )}
      <div className="flex items-center gap-2">
        {phone.mobileCode && (
          <div className={"flex gap-2"}>
            <Flag
              countryCode={_.invert(PHONE_COUNTRY_CODES)[phone.mobileCode]}
              className="!w-6 !h-6 rounded-sm"
            />
            <Base title={`${phone.mobileCode}`}>{phone.mobileCode}</Base>
          </div>
        )}

        <Base
          className={twMerge("overflow-hidden whitespace-nowrap break-normal")}
          title={`${phoneNumber}`}
        >
          {`${phoneNumber}`}
        </Base>
      </div>
    </AliasCard>
  );
};
